import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
  Switch,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { willService } from '../../../../services/willInfo';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('WillInput');

type WillInputScreenRouteProp = RouteProp<RootStackParamList, 'WillInput'>;

export const WillInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<WillInputScreenRouteProp>();

  const { willData, isEditing } = route.params || {};

  // Helper: format date as MM/DD/YYYY while typing
  const formatDate = (text: string) => {
    const cleaned = text.replace(/\D/g, '');
    if (cleaned.length >= 8) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4, 8)}`;
    } else if (cleaned.length >= 4) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4)}`;
    } else if (cleaned.length >= 2) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
    }
    return cleaned;
  };

  // Helper: format phone as (xxx) xxx-xxxx while typing
  const formatPhoneNumber = (text: string) => {
    const cleaned = (text || '').replace(/\D/g, '');
    if (cleaned.length >= 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    } else if (cleaned.length >= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length >= 3) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    }
    return cleaned;
  };

  const [physicalLocation, setPhysicalLocation] = useState(willData?.physicalLocation || '');
  const [dateSigned, setDateSigned] = useState(willData?.dateSigned ? formatDate(willData.dateSigned.replace(/\D/g, '')) : '');
  const [executorName, setExecutorName] = useState(willData?.executorName || '');
  const [executorContact, setExecutorContact] = useState(willData?.executorContact ? formatPhoneNumber(willData.executorContact) : '');
  const [alternateExecutorName, setAlternateExecutorName] = useState(willData?.alternateExecutorName || '');
  const [alternateExecutorContact, setAlternateExecutorContact] = useState(willData?.alternateExecutorContact ? formatPhoneNumber(willData.alternateExecutorContact) : '');
  const [attorneyName, setAttorneyName] = useState(willData?.attorneyName || '');
  const [attorneyContact, setAttorneyContact] = useState(willData?.attorneyContact ? formatPhoneNumber(willData.attorneyContact) : '');
  const [specificInstructions, setSpecificInstructions] = useState(willData?.specificInstructions || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!physicalLocation.trim()) {
      Alert.alert('Error', 'Please enter where your Will is located');
      return;
    }

    if (!executorName.trim()) {
      Alert.alert('Error', 'Please enter your executor\'s name');
      return;
    }

    try {
      setSaving(true);

      const data = {
        physicalLocation: physicalLocation.trim(),
        dateSigned: dateSigned.trim(),
        executorName: executorName.trim(),
        executorContact: executorContact.trim(),
        alternateExecutorName: alternateExecutorName.trim(),
        alternateExecutorContact: alternateExecutorContact.trim(),
        attorneyName: attorneyName.trim(),
        attorneyContact: attorneyContact.trim(),
        specificInstructions: specificInstructions.trim(),
      };

      let savedData;
      if (isEditing && willData?.id) {
        savedData = await willService.updateWill(willData.id, data);
        const updatedData = savedData?.data || savedData;
        logger.debug('Will updated successfully with ID:', updatedData.id);
        navigation.navigate('WillLockUnlock', { willId: updatedData.id });
      } else {
        savedData = await willService.createWill(data);
        const createdData = savedData?.data || savedData;
        logger.debug('Will saved successfully with ID:', createdData.id);
        navigation.navigate('WillLockUnlock', { willId: createdData.id });
      }
    } catch (error) {
      logger.error('Error saving will:', error);
      Alert.alert('Error', 'Failed to save will information. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Image source={require('../../../../assets/icons/back.png')} style={[styles.backIcon, { tintColor: '#fff' }]} />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image source={require('../../../../assets/icons/keyimage.png')} style={styles.backgroundKey} />
        <Image source={require('../../../../assets/logo.png')} style={styles.logo} resizeMode="contain" />
        <Text style={styles.title}>Add Your Will Details</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView}>
          <Text style={styles.sectionTitle}>Will Information:</Text>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Date Signed:</Text>
            <TextInput
              style={styles.input}
              value={dateSigned}
              onChangeText={(text) => setDateSigned(formatDate(text))}
              placeholder="MM/DD/YYYY"
              placeholderTextColor="#A9A9A9"
              keyboardType="number-pad"
              maxLength={10}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Document Location:</Text>
            <TextInput
              style={styles.input}
              value={physicalLocation}
              onChangeText={setPhysicalLocation}
              placeholder="e.g., Safe deposit box at XYZ Bank"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <Text style={styles.sectionTitle}>Primary Executor:</Text>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Full Name:</Text>
            <TextInput
              style={styles.input}
              value={executorName}
              onChangeText={setExecutorName}
              placeholder="Enter executor's full name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Contact Phone:</Text>
            <TextInput
              style={styles.input}
              value={executorContact}
              onChangeText={(text) => setExecutorContact(formatPhoneNumber(text))}
              placeholder="(xxx) xxx-xxxx"
              placeholderTextColor="#A9A9A9"
              keyboardType="number-pad"
              maxLength={14}
            />
          </View>

          <Text style={styles.sectionTitle}>Alternate Executor (Optional):</Text>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Full Name:</Text>
            <TextInput
              style={styles.input}
              value={alternateExecutorName}
              onChangeText={setAlternateExecutorName}
              placeholder="Enter alternate executor's name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Contact Phone:</Text>
            <TextInput
              style={styles.input}
              value={alternateExecutorContact}
              onChangeText={(text) => setAlternateExecutorContact(formatPhoneNumber(text))}
              placeholder="(xxx) xxx-xxxx"
              placeholderTextColor="#A9A9A9"
              keyboardType="number-pad"
              maxLength={14}
            />
          </View>

          <Text style={styles.sectionTitle}>Attorney (Optional):</Text>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Attorney Name:</Text>
            <TextInput
              style={styles.input}
              value={attorneyName}
              onChangeText={setAttorneyName}
              placeholder="Enter attorney's name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Attorney Contact Phone:</Text>
            <TextInput
              style={styles.input}
              value={attorneyContact}
              onChangeText={(text) => setAttorneyContact(formatPhoneNumber(text))}
              placeholder="(xxx) xxx-xxxx"
              placeholderTextColor="#A9A9A9"
              keyboardType="number-pad"
              maxLength={14}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Specific Instructions (Optional):</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={specificInstructions}
              onChangeText={setSpecificInstructions}
              placeholder="Any additional notes about your will"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.submitButtonContainer}>
            <TouchableOpacity
              style={[styles.saveButton, saving && { opacity: 0.7 }]}
              onPress={handleSave}
              disabled={saving}
            >
              {saving ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.saveButtonText}>{isEditing ? 'Update' : 'Submit'}</Text>
              )}
            </TouchableOpacity>
          </View>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#29BFC0' },
  header: { flexDirection: 'row', alignItems: 'center', padding: ms(15), paddingTop: ms(25), position: 'relative' },
  backButton: { position: 'absolute', left: ms(50), padding: ms(15), paddingTop: ms(50), zIndex: 1 },
  backIcon: { width: s(32), height: s(32), tintColor: '#fff' },
  topSection: { alignItems: 'center', paddingVertical: ms(20) },
  backgroundKey: { position: 'absolute', width: '85%', height: '85%', opacity: 0.1 },
  logo: { width: s(125), height: s(125), marginBottom: ms(16) },
  title: { fontSize: ms(24, 0.3), fontWeight: 'bold', color: '#FFFFFF', marginBottom: ms(8), paddingHorizontal: ms(30), textAlign: 'center' },
  inputContainer: { flex: 1, backgroundColor: '#F3F6F8', borderTopLeftRadius: ms(20), borderTopRightRadius: ms(20), paddingTop: ms(20) },
  scrollView: { paddingHorizontal: ms(16), paddingBottom: ms(120) },
  sectionTitle: { fontSize: ms(20, 0.3), fontWeight: 'bold', color: '#1C2C4F', marginBottom: ms(16), marginTop: ms(10) },
  inputGroup: { marginBottom: ms(16) },
  label: { fontSize: ms(16, 0.3), fontWeight: '600', color: '#6B7280', marginBottom: ms(8) },
  input: { backgroundColor: '#FFFFFF', borderRadius: ms(25), padding: ms(15), fontSize: ms(16, 0.3), color: '#1C2C4F', borderWidth: ms(1), borderColor: '#d8dbdd' },
  textArea: { minHeight: s(80), textAlignVertical: 'top' },
  submitButtonContainer: { alignItems: 'center', marginTop: ms(20), marginBottom: ms(40) },
  saveButton: { backgroundColor: '#29BFC0', borderRadius: ms(25), paddingVertical: ms(15), paddingHorizontal: ms(60), alignItems: 'center', marginBottom: ms(120), minWidth: s(200) },
  saveButtonText: { color: '#FFFFFF', fontSize: ms(16, 0.3), fontWeight: '600' },
  bottomPadding: { height: s(120) },
});
